import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Box, MenuItem } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { decoratedFetch } from "../req_utils";
import { updateProjectFinancialInfo } from "../reducers";
import { CMAS_TYPES } from "../constants";
import ThemedSelect from "./ThemedSelect";
import ThemedTextField from "./ThemedTextField";
import ThemedTypography from "./ThemedTypography";
import ThemedPaper from "./ThemedPaper";
import ThemedButton from "./ThemedButton";

const FinancialInfoTab = ({ value, index, projectId, accountId }) => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const [proposals, setProposals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [financialData, setFinancialData] = useState({
    invoicedProposalId: "",
    invoicedProposalPrice: "",
    jobCostingProposalId: "",
    jobCostingProposalPrice: "",
    consultantId: "",
    cmasType: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load proposals for the project
  useEffect(() => {
    if (value !== index || !projectId) return;

    setLoading(true);
    decoratedFetch(`/list_proposals/${projectId}?financial_info=true`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Failed to fetch proposals");
        }
        return response.json();
      })
      .then((data) => {
        // Merge proposals from API with any proposals already in project data
        let allProposals = [...data];

        // Add invoiced proposal if it exists and isn't already in the list
        if (
          project?.invoiced_proposal &&
          !allProposals.find((p) => p.id === project.invoiced_proposal.id)
        ) {
          allProposals.push(project.invoiced_proposal);
        }

        // Add job costing proposal if it exists and isn't already in the list
        if (
          project?.job_costing_proposal &&
          !allProposals.find((p) => p.id === project.job_costing_proposal.id)
        ) {
          allProposals.push(project.job_costing_proposal);
        }

        setProposals(allProposals);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError("Failed to load proposals. Please try again.");
      });
  }, [
    value,
    index,
    projectId,
    project?.invoiced_proposal?.id,
    project?.job_costing_proposal?.id,
  ]);

  // Load contacts for the project
  useEffect(() => {
    if (value !== index || !accountId) return;

    setLoading(true);
    let url = `/list_contacts?project_id=${projectId}&account_id=${accountId}`;

    decoratedFetch(url)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Failed to fetch contacts");
        }
        return response.json();
      })
      .then((data) => {
        setContacts(data);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError("Failed to load contacts. Please try again.");
      });
  }, [value, index, accountId, projectId]);

  // Sync local state with Redux project data
  useEffect(() => {
    if (project) {
      setFinancialData({
        invoicedProposalId: project.invoiced_proposal?.id || "",
        invoicedProposalPrice: project.invoiced_proposal_price || "",
        jobCostingProposalId: project.job_costing_proposal?.id || "",
        jobCostingProposalPrice: project.job_costing_proposal_price || "",
        consultantId: project.consultant?.id || "",
        cmasType: project.cmas_type || "",
      });
    }
  }, [
    project,
    project?.invoiced_proposal?.id,
    project?.invoiced_proposal_price,
    project?.job_costing_proposal?.id,
    project?.job_costing_proposal_price,
    project?.consultant?.id,
    project?.cmas_type,
  ]);

  // Initialize financial data when component mounts or project changes
  useEffect(() => {
    if (project && value === index && proposals.length > 0) {
      setFinancialData({
        invoicedProposalId: project.invoiced_proposal?.id || "",
        invoicedProposalPrice: project.invoiced_proposal_price || "",
        jobCostingProposalId: project.job_costing_proposal?.id || "",
        jobCostingProposalPrice: project.job_costing_proposal_price || "",
        consultantId: project.consultant?.id || "",
        cmasType: project.cmas_type || "",
      });
    }
  }, [project, value, index, proposals.length]);

  // Handle proposal selection for invoiced proposal
  const handleInvoicedProposalChange = (event) => {
    const selectedProposalId = event.target.value;
    const selectedProposal = proposals.find((p) => p.id === selectedProposalId);

    const newData = {
      invoiced_proposal_id: selectedProposalId,
      invoiced_proposal_price: selectedProposal?.proposal_total || 0,
    };
    
    setFinancialData((prev) => ({
      ...prev,
      invoicedProposalId: selectedProposalId,
      invoicedProposalPrice: selectedProposal?.proposal_total || "",
    }));

    dispatch(updateProjectFinancialInfo(newData));
  };

  // Handle proposal selection for job costing
  const handleJobCostingProposalChange = (event) => {
    const selectedProposalId = event.target.value;
    const selectedProposal = proposals.find((p) => p.id === selectedProposalId);

    const newData = {
      job_costing_proposal_id: selectedProposalId,
      job_costing_proposal_price: selectedProposal?.proposal_total || 0,
    };

    setFinancialData((prev) => ({
      ...prev,
      jobCostingProposalId: selectedProposalId,
      jobCostingProposalPrice: selectedProposal?.proposal_total || "",
    }));

    dispatch(updateProjectFinancialInfo(newData));
  };

  // Handle consultant selection
  const handleConsultantChange = (event) => {
    const consultantId = event.target.value;

    setFinancialData((prev) => ({
      ...prev,
      consultantId,
    }));

    dispatch(updateProjectFinancialInfo({ consultant_id: consultantId }));
  };

  // Handle CMAS type selection
  const handleCmasTypeChange = (event) => {
    const cmasType = event.target.value;

    setFinancialData((prev) => ({
      ...prev,
      cmasType,
    }));

    dispatch(updateProjectFinancialInfo({ cmas_type: cmasType }));
  };

  // Handle price changes
  const handleInvoicedPriceChange = (event) => {
    const price = event.target.value;

    setFinancialData((prev) => ({
      ...prev,
      invoicedProposalPrice: price,
    }));
  };

  const handleInvoicedPriceBlur = (event) => {
    const price = event.target.value;
    const numericPrice = price === "" ? 0 : parseFloat(price) || 0;

    dispatch(updateProjectFinancialInfo({ invoiced_proposal_price: numericPrice }));
  };

  const handleJobCostingPriceChange = (event) => {
    const price = event.target.value;

    setFinancialData((prev) => ({
      ...prev,
      jobCostingProposalPrice: price,
    }));
  };

  const handleJobCostingPriceBlur = (event) => {
    const price = event.target.value;
    const numericPrice = price === "" ? 0 : parseFloat(price) || 0;

    dispatch(updateProjectFinancialInfo({ job_costing_proposal_price: numericPrice }));
  };

  if (value !== index) {
    return null;
  }

  if (loading || !project) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <ThemedTypography>Loading financial information...</ThemedTypography>
      </Box>
    );
  }

  if (proposals.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <ThemedTypography>Loading proposals...</ThemedTypography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <ThemedTypography color="error">{error}</ThemedTypography>
        <ThemedButton
          onClick={() => setError("")}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Try Again
        </ThemedButton>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ThemedTypography variant="h6" sx={{ mb: 3 }}>
        Financial Information
      </ThemedTypography>

      <Grid container spacing={3}>
        {/* Invoiced Proposal Section */}
        <Grid item xs={12} md={6}>
          <ThemedPaper sx={{ p: 2, height: "100%" }}>
            <ThemedTypography
              variant="subtitle1"
              sx={{ mb: 2, color: "#002466", fontWeight: "bold" }}
            >
              Invoiced Proposal
            </ThemedTypography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ThemedSelect
                  label="Select Proposal"
                  value={project?.invoiced_proposal?.id || ""}
                  onChange={handleInvoicedProposalChange}
                  fullWidth
                  size="small"
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">
                    <em>Select a proposal</em>
                  </MenuItem>
                  {proposals.map((proposal) => (
                    <MenuItem key={proposal.id} value={proposal.id}>
                      {proposal.name}
                    </MenuItem>
                  ))}
                </ThemedSelect>
              </Grid>

              <Grid item xs={12}>
                <ThemedTextField
                  label="Proposal Price"
                  value={financialData.invoicedProposalPrice}
                  onChange={handleInvoicedPriceChange}
                  onBlur={handleInvoicedPriceBlur}
                  fullWidth
                  size="small"
                  variant="standard"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ marginRight: "4px" }}
                      >
                        <span style={{ marginTop: "-3.5px", fontSize: "14px" }}>
                          $
                        </span>
                      </InputAdornment>
                    ),
                    disableUnderline: true,
                  }}
                />
              </Grid>
            </Grid>
          </ThemedPaper>
        </Grid>

        {/* Job Costing Section */}
        <Grid item xs={12} md={6}>
          <ThemedPaper sx={{ p: 2, height: "100%" }}>
            <ThemedTypography
              variant="subtitle1"
              sx={{ mb: 2, color: "#002466", fontWeight: "bold" }}
            >
              Job Costing
            </ThemedTypography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ThemedSelect
                  label="Select Proposal"
                  value={project?.job_costing_proposal?.id || ""}
                  onChange={handleJobCostingProposalChange}
                  fullWidth
                  size="small"
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">
                    <em>Select a proposal</em>
                  </MenuItem>
                  {proposals.map((proposal) => (
                    <MenuItem key={proposal.id} value={proposal.id}>
                      {proposal.name}
                    </MenuItem>
                  ))}
                </ThemedSelect>
              </Grid>

              <Grid item xs={12}>
                <ThemedTextField
                  label="Proposal Price"
                  value={financialData.jobCostingProposalPrice}
                  onChange={handleJobCostingPriceChange}
                  onBlur={handleJobCostingPriceBlur}
                  fullWidth
                  size="small"
                  variant="standard"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ marginRight: "4px" }}
                      >
                        <span style={{ marginTop: "-3px", fontSize: "14px" }}>
                          $
                        </span>
                      </InputAdornment>
                    ),
                    disableUnderline: true,
                  }}
                />
              </Grid>
            </Grid>
          </ThemedPaper>
        </Grid>

        {/* Consultant Section */}
        <Grid item xs={12}>
          <ThemedPaper sx={{ p: 2 }}>
            <ThemedTypography
              variant="subtitle1"
              sx={{ mb: 2, color: "#002466", fontWeight: "bold" }}
            >
              Consultant
            </ThemedTypography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ThemedSelect
                  label="Select Consultant"
                  value={project?.consultant?.id || ""}
                  onChange={handleConsultantChange}
                  fullWidth
                  size="small"
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">
                    <em>Select a consultant</em>
                  </MenuItem>
                  {contacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </MenuItem>
                  ))}
                </ThemedSelect>
              </Grid>
            </Grid>
          </ThemedPaper>
        </Grid>

        {/* CMAS Type Section */}
        <Grid item xs={12}>
          <ThemedPaper sx={{ p: 2 }}>
            <ThemedTypography
              variant="subtitle1"
              sx={{ mb: 2, color: "#002466", fontWeight: "bold" }}
            >
              CMAS
            </ThemedTypography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ThemedSelect
                  label="Select CMAS Type"
                  value={project?.cmas_type || ""}
                  onChange={handleCmasTypeChange}
                  fullWidth
                  size="small"
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">
                    <em>Select a CMAS type</em>
                  </MenuItem>
                  {CMAS_TYPES.map((cmasType) => (
                    <MenuItem key={cmasType} value={cmasType}>
                      {cmasType}
                    </MenuItem>
                  ))}
                </ThemedSelect>
              </Grid>
            </Grid>
          </ThemedPaper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinancialInfoTab;
